import { v4 as uuidv4 } from 'uuid';

type UploadState = 'isPending' | 'isError' | 'isSuccess' | 'initial';

export type FileSelection = {
  file: File;
  id: string;
  uploadState: UploadState;
  error?: string;
  extractedMetaData?: {
    isInitial: boolean;
    value: string | [];
  };
};
export type FileSelectionState = FileSelection[];

type AddFileSelections = {
  type: 'addFileSelections';
  payload: File[];
};
type AddFileSelectionsWithExtractedMetaData = {
  type: 'addFileSelectionsWithMetadata';
  payload: { file: File; metaData?: string }[];
};
type RemoveFileSelections = {
  type: 'removeFileSelections';
  payload: string[];
};
type ResetFileSelections = {
  type: 'resetFileSelections';
};
type UpdateFileSelectionUploadState = {
  type: 'updateFileSelectionUploadState';
  payload: {
    id: string;
    newState: UploadState;
    error?: string;
  };
};
type UpdateFileMetaData = {
  type: 'updateFileMetaData';
  payload: { id: string; value: string | [] };
};
type UpdateAllUploadStates = {
  type: 'updateAllUploadStates';
  payload: { newState: UploadState };
};

export type FileSelectionAction =
  | AddFileSelections
  | AddFileSelectionsWithExtractedMetaData
  | RemoveFileSelections
  | ResetFileSelections
  | UpdateFileSelectionUploadState
  | UpdateAllUploadStates
  | UpdateFileMetaData;

export const fileReducer = (
  state: FileSelectionState,
  action: FileSelectionAction
): FileSelectionState => {
  switch (action.type) {
    case 'addFileSelections': {
      return [
        ...state,
        ...action.payload.map(
          (file): FileSelection => ({
            file,
            uploadState: 'initial',
            id: uuidv4(),
          })
        ),
      ];
    }
    case 'addFileSelectionsWithMetadata': {
      return [
        ...state,
        ...action.payload.map(
          (data): FileSelection => ({
            file: data?.file,
            uploadState: 'initial',
            id: uuidv4(),
            extractedMetaData: {
              isInitial: true,
              value: data?.metaData || '',
            },
          })
        ),
      ];
    }
    case 'removeFileSelections': {
      return state.filter(
        (selection) => !action.payload.includes(selection.id)
      );
    }
    case 'resetFileSelections': {
      return [];
    }
    case 'updateFileMetaData': {
      return state.map((file) =>
        file.id === action.payload.id
          ? {
              ...file,
              extractedMetaData: {
                isInitial: false,
                value: action.payload.value,
              },
            }
          : file
      );
    }
    case 'updateFileSelectionUploadState': {
      return state.map((selection) =>
        selection.id === action.payload.id
          ? {
              ...selection,
              uploadState: action.payload.newState,
              error: action.payload.error ?? undefined,
            }
          : selection
      );
    }
    case 'updateAllUploadStates': {
      return state.map((s) => ({ ...s, uploadState: action.payload.newState }));
    }
    default: {
      const exhaustionCheck: never = action;
      console.error(
        'Unexpected action type in function "fileReducer":',
        exhaustionCheck
      );

      return state;
    }
  }
};
